<template>
  <NotFound
    v-if="isNotFound"
    :title="localize('player.not_found.title')"
    :username="String(route.params.username)"
    no-close
  >
    <GamButton @click="navigateToSparks" :variant="GamButtonVariant.SECONDARY" :size="GamButtonSize.SMALL" is-centered>
      {{ localize('player.not_found.explore') }}
    </GamButton>
  </NotFound>

  <template v-else-if="playerDetails">
    <div :class="['detail-container', 'player', { 'is-mobile': isMobile }]">
      <!-- As tall as the sheet, so the stack can stick for the whole scroll. -->
      <div v-if="isMyProfile" class="profile-nav-rail">
        <NavPill class="profile-nav" vertical>
          <NavPillButton :aria-label="localize('app.nav.walk')" @select="goToWalk">
            <GamIcon :name="GamIconName.WALK_MARK" :size="GamIconSize.LARGE" is-current />
          </NavPillButton>
          <NavPillButton class="clock-lead" :aria-label="localize('app.nav.clock')" @select="goToClock">
            <span class="clock-disc">
              <GamIcon :name="GamIconName.CHESS_CLOCK" :size="GamIconSize.X_LARGE" is-current />
            </span>
          </NavPillButton>
          <NavPillButton :aria-label="localize('handshake.code.getCode')" @select="openHandshakeQr">
            <GamIcon :name="GamIconName.HANDSHAKE_EMPTY" :size="GamIconSize.LARGE" is-current />
          </NavPillButton>
        </NavPill>
      </div>

      <div class="base-info">
        <div class="header-container">
          <GamPlayerDetailHeader
            :player="playerDetails"
            @update="getPlayerDetail"
            @settings="router.push({ name: RouterNameType.PROFILE })"
            @players="openPlayersFromHeader"
            @challenge="startProposal"
            @username-click="toggleDetailsPanel"
          >
            <template #pendant>
              <HandshakeButton
                v-if="isMyProfile"
                ref="sphereRef"
                :aria-label="handshakeCountsLabel"
                :responder-id="null"
                :is-completed="false"
                :is-pending="false"
                :pair-count="collectionCounts.pairs"
                :is-master="collectionCounts.isMaster"
                is-badge
                @click="openCollectionFromSphere"
              />
              <div v-else class="pendant-play">
                <GamButton v-bind="getPlayCtaProps" />
              </div>
            </template>

            <template #top-left>
              <span class="profile-corner">
                <span class="profile-unpaired" :aria-label="unpairedLabel">
                  <span class="un">{{ localize('player.unpaired.accent') }}</span
                  ><span>{{ localize('player.unpaired.rest') }}</span>
                  <span class="count">{{ collectionCounts.unpaired }}</span>
                </span>
                <button
                  v-if="!isMyProfile"
                  type="button"
                  class="profile-bond"
                  :class="{ 'is-unique': isUniquePair, 'is-repeat': isPaired && !isUniquePair }"
                  @click="openCollection('overview')"
                >
                  {{ localize(isPaired ? 'player.paired' : 'player.weAreUnpaired') }}
                </button>
              </span>
            </template>

            <template #top-right-actions>
              <PlayerActions v-if="!isMyProfile" ref="playerActionsRef" :player="playerDetails" />
            </template>
          </GamPlayerDetailHeader>

          <div v-if="playerDetails && showDetailsPanel" ref="detailsPanelRef" class="age-and-follows-section">
            <div v-if="playerDetails.username" class="panel-username">@{{ playerDetails.username }}</div>
            <div class="name-and-age">
              <span class="player-name">{{ playerDetails.lastName }} {{ playerDetails.firstName }}</span>
              <span class="age-comma">,</span>
              <span class="age">{{ getHumanizedAge(playerDetails) }}</span>
            </div>
            <div v-if="playerDetails?.aboutMe" class="about-me">
              {{ playerDetails.aboutMe }}
            </div>
          </div>
        </div>

        <slot name="pull-to-refresh" />
      </div>

      <div class="detail-info">
        <div class="latest-handshakes" :class="{ 'has-gambits': hasVisibleSections }">
          <div v-if="hasVisibleSections" class="profile-gambits">
            <ProfileCardSection
              v-if="isMyProfile && (respond.items.value.length > 0 || respond.isLoading.value)"
              :title="localize('player.sections.respond')"
              title-color="var(--color-pink)"
            >
              <template v-if="respond.isLoading.value && !respond.items.value.length">
                <GamGambitResultCardSkeleton v-for="i in GAMBIT_BATCH_SIZE" :key="'respond-skel-' + i" />
              </template>
              <template v-else>
                <template
                  v-for="item in respond.items.value"
                  :key="'respond-' + (item.kind === 'proposal' ? item.proposal.id : item.gambit.id)"
                >
                  <ProposalProfileCard
                    v-if="item.kind === 'proposal'"
                    class="needs-respond"
                    :proposal="item.proposal"
                    :date="item.date"
                  />
                  <GamGambitResultCard
                    v-else
                    class="needs-respond"
                    :gambit="item.gambit"
                    :location="item.gambit.location || null"
                    :show-distance="false"
                    :respond-badge="item.respondBadge"
                    is-link
                  />
                </template>
                <div v-if="respond.hasMore.value" ref="respondSentinel" class="gambit-sentinel">
                  <GamGambitResultCardSkeleton />
                </div>
              </template>
            </ProfileCardSection>

            <ProfileCardSection
              v-if="upcoming.items.value.length > 0 || upcoming.isLoading.value"
              :title="upcoming.items.value.length > 0 ? localize('player.sections.upcoming') : ''"
              title-color="var(--color-white)"
            >
              <template v-if="upcoming.isLoading.value && !upcoming.items.value.length">
                <GamGambitResultCardSkeleton v-for="i in GAMBIT_BATCH_SIZE" :key="'skel-' + i" />
              </template>
              <template v-else>
                <GamGambitResultCard
                  v-for="gambit in upcoming.items.value"
                  :gambit="gambit"
                  :location="gambit.location || null"
                  :key="gambit.id"
                  :show-distance="false"
                  is-link
                />
                <div v-if="upcoming.hasMore.value" ref="gambitSentinel" class="gambit-sentinel">
                  <GamGambitResultCardSkeleton />
                </div>
              </template>
            </ProfileCardSection>

            <ProfileCardSection
              v-if="isMyProfile && (pending.items.value.length > 0 || pending.isLoading.value)"
              :title="localize('player.sections.pending')"
            >
              <template v-if="pending.isLoading.value && !pending.items.value.length">
                <GamGambitResultCardSkeleton v-for="i in GAMBIT_BATCH_SIZE" :key="'pending-skel-' + i" />
              </template>
              <template v-else>
                <GamGambitResultCard
                  v-for="item in pending.items.value"
                  :gambit="item.gambit"
                  :location="item.gambit.location || null"
                  :key="'pending-' + item.gambit.id"
                  :show-distance="false"
                  :respond-badge="item.pendingBadge"
                  is-link
                />
                <div v-if="pending.hasMore.value" ref="pendingSentinel" class="gambit-sentinel">
                  <GamGambitResultCardSkeleton />
                </div>
              </template>
            </ProfileCardSection>
          </div>

          <HandshakeList
            v-if="playerDetails?.handshakes"
            ref="handshakeList"
            :handshakes="playerDetails.handshakes"
            :user-id="playerDetails.id"
            :starts-striped="profileBandCount % 2 === 0"
            :title="localize('player.sections.handshakes')"
            show-time-and-occasion
            enable-game-suggestion
            @update="refreshPlayerDetail"
          />
        </div>

        <PlayersModal
          v-if="isMyProfile"
          :visible="showPlayersModal"
          :initial-tab="playersModalTab"
          @close="closePlayersModal"
        />

        <HandshakeCollectionModal
          v-if="playerDetails"
          :visible="showCollection"
          :user-id="playerDetails.id"
          :handshakes="playerDetails.handshakes || []"
          :profile-image="playerDetails.profileImageBlobHash"
          :initial-tab="collectionTab"
          :origin="collectionOrigin"
          @close="closeCollection"
          @update="refreshProfileOnly"
        />

        <BoardsOverlay
          v-if="isMyProfile"
          :visible="showBoardsOverlay"
          :initial-tab="boardsTab"
          @close="closeBoardsOverlay"
          @tab-change="onBoardsTabChange"
        />
      </div>
    </div>

    <HandshakeQRModal
      v-if="isMyProfile"
      ref="handshakeQrModalRef"
      @visible-change="onQrVisibleChange"
      @handshake-created="onHandshakeCreated"
    />
  </template>
</template>

<script setup lang="ts">
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { BaseGambitDto } from '@/core/data/gambit/gambit.interface';
import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import type { SegmentProposal } from '@/core/data/spark/spark.interface';
import type { AttendeeDto, BaseUserDto, HandshakeFields, UserDto } from '@/core/data/user/user.interface';
import { gambits, localize } from '@/core/gambits';
import { createLogger } from '@/core/helpers/logger.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import { firestore } from '@/core/network/firebase';
import { useAuthStore } from '@/stores/auth.store';
import { useMapStore } from '@/stores/map.store';
import { usePlayerStore } from '@/stores/player.store';
import { useTableStore } from '@/stores/table.store';
import GamGambitResultCard from '@/views/components/gam-gambits/GamGambitResultCard.vue';
import GamGambitResultCardSkeleton from '@/views/components/gam-gambits/GamGambitResultCardSkeleton.vue';
import GamPlayerDetailHeader from '@/views/components/gam-player/GamPlayerDetailHeader.vue';
import { getHumanizedAge } from '@/views/components/gam-player/helpers';
import PlayerActions from '@/views/components/gam-player/PlayerActions.vue';
import GamButton from '@/views/components/GamButton.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import NavPill from '@/views/components/NavPill.vue';
import NavPillButton from '@/views/components/NavPillButton.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { useBatchLoader } from '@/views/composables/hooks/useBatchLoader';
import { useConfetti } from '@/views/composables/hooks/useConfetti';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import { openLiveHandshakeClock } from '@/views/main/clock/utils/open-handshake-clock';
import HandshakeButton from '@/views/main/handshakes/HandshakeButton.vue';
import HandshakeCollectionModal from '@/views/main/handshakes/HandshakeCollectionModal.vue';
import HandshakeList from '@/views/main/handshakes/HandshakeList.vue';
import {
  celebrateNewlyConfirmedHandshakes,
  rememberHandshake,
} from '@/views/main/handshakes/utils/handshake-celebration';
import { collectPositions } from '@/views/main/handshakes/utils/position-collection';

import { useGhostClickGuard } from '@/views/composables/hooks/useGhostClickGuard';
import {
  isHandshakeModalOpen,
  markHandshakeModalClosed,
  markHandshakeModalOpen,
  markHandshakeShown,
  wasHandshakeShown,
} from '@/views/main/handshakes/handshake-modal-queue';
import HandshakeModal from '@/views/main/handshakes/HandshakeModal.vue';
import HandshakeQRModal from '@/views/main/handshakes/HandshakeQRModal.vue';
import BoardsOverlay from '@/views/main/locations/modals/BoardsOverlay.vue';
import PlayersModal from '@/views/main/players/modals/PlayersModal.vue';
import ProfileCardSection from '@/views/main/players/sections/ProfileCardSection.vue';
import ProposalProfileCard from '@/views/main/players/sections/ProposalProfileCard.vue';
import NotFound from '@/views/NotFound.vue';
import { doc, onSnapshot } from 'firebase/firestore';
import { storeToRefs } from 'pinia';
import {
  computed,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  onMounted,
  ref,
  watch,
  type ComponentPublicInstance,
  type Ref,
} from 'vue';
import { useModal } from 'vue-final-modal';
import { useRoute, useRouter } from 'vue-router';

const logger = createLogger('PlayerDetail');

interface PlayerDetailsProps {
  username?: string;
  isDetailView?: boolean;
}

const props = defineProps<PlayerDetailsProps>();

const listId = GamListId.PLAYERS;
const tableStore = useTableStore<BaseUserDto>(listId)();
const { itemList, isLoading: isListLoading, tableParams } = storeToRefs(tableStore);

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);
const { fire: fireConfetti } = useConfetti();
const mapStore = useMapStore();
const playerStore = usePlayerStore();

const route = useRoute();
const router = useRouter();

const isDetailLoading = ref<boolean>(false);
const isNotFound = ref<boolean>(false);
const playerDetails = ref<UserDto | null>(null);

const playerActionsRef = ref<InstanceType<typeof PlayerActions>>();
const handshakeQrModalRef = ref<InstanceType<typeof HandshakeQRModal>>();
const showDetailsPanel = ref(false);

function openHandshakeQr() {
  handshakeQrModalRef.value?.show();
}

function goToWalk() {
  router.push({ name: RouterNameType.WALK });
}

function goToClock() {
  router.push({ name: RouterNameType.CLOCK });
}

const detailsPanelRef = ref<HTMLDivElement>();
const showPlayersModal = ref(false);
const showBoardsOverlay = ref(false);
const boardsTab = ref<'ongoing' | 'completed'>('ongoing');
const playersModalTab = ref<'followers' | 'following'>('following');
const showCollection = ref(false);
const collectionOrigin = ref<{ x: number; y: number; size: number } | null>(null);
const collectionTab = ref<'overview' | 'positions' | 'milestones' | 'people' | 'handshakes' | 'boards'>('overview');
const sphereRef = ref<ComponentPublicInstance>();

function closeBoardsOverlay() {
  showBoardsOverlay.value = false;
  router.replace({ ...route, query: { ...route.query, boards: undefined } });
}

function onBoardsTabChange(tab: 'ongoing' | 'completed') {
  boardsTab.value = tab;
  router.replace({ ...route, query: { ...route.query, boards: tab } });
}

function openCollection(
  tab: 'overview' | 'positions' | 'milestones' | 'people' | 'handshakes' | 'boards',
  fromSphere = false,
) {
  if (!fromSphere) collectionOrigin.value = null;
  collectionTab.value = tab;
  showCollection.value = true;
  showPlayersModal.value = false;
  router.replace({ ...route, query: { ...route.query, players: tab } });
}

function openCollectionFromSphere() {
  const el = sphereRef.value?.$el as HTMLElement | undefined;
  const rect = el?.getBoundingClientRect();
  collectionOrigin.value = rect
    ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2, size: Math.min(rect.width, rect.height) }
    : null;
  openCollection('overview', true);
}

function closeCollection() {
  showCollection.value = false;
  const players = route.query.players;
  if (
    players === 'handshakes' ||
    players === 'positions' ||
    players === 'overview' ||
    players === 'milestones' ||
    players === 'people' ||
    players === 'boards'
  ) {
    router.replace({ ...route, query: { ...route.query, players: undefined } });
  }
}

function openPlayersFromHeader() {
  router.replace({ ...route, query: { ...route.query, players: 'following' } });
}

function openPlayersModal(tab: 'followers' | 'following') {
  playersModalTab.value = tab;
  showPlayersModal.value = true;
}

function closePlayersModal() {
  showPlayersModal.value = false;
  if (route.query.players === 'followers' || route.query.players === 'following') {
    router.replace({ ...route, query: { ...route.query, players: undefined } });
  }
}

const startProposal = () => {
  if (isMyProfile.value) {
    mapStore.enterProposalMode();
  } else {
    if (!playerDetails.value) return;
    mapStore.enterProposalMode({
      challengeUserId: playerDetails.value.id,
      challengeUsername: playerDetails.value.username,
    });
  }
  router.push({ path: '/discover' });
};

const getPlayCtaProps = computed(
  (): GamButtonType => ({
    size: GamButtonSize.LARGE,
    leftIcon: GamIconName.PLAY_EMPTY,
    variant: GamButtonVariant.HYPE,
    isIconOnly: true,
    isDropShadow: true,
    containerClass: 'circle-cta',
    onClick: () => startProposal(),
  }),
);

const toggleDetailsPanel = () => {
  showDetailsPanel.value = !showDetailsPanel.value;
};

const onClickOutsideDetails = (e: MouseEvent) => {
  if (!showDetailsPanel.value) return;
  const target = e.target as HTMLElement;
  if (detailsPanelRef.value?.contains(target)) return;
  if (target.closest('.pill')) return;
  showDetailsPanel.value = false;
};

const GAMBIT_BATCH_SIZE = 4;
const loadGeneration = ref(0);

async function fetchGambitsById(refs: Array<{ id: string }>): Promise<(BaseGambitDto | null)[]> {
  const results = await Promise.all(refs.map((g) => gambits.gambitService.getGambit(g.id)));
  return results.map((r) => (r?.data ? (r.data as unknown as BaseGambitDto) : null));
}

type RespondRef =
  | { kind: 'gambit'; id: string; invitationType: 'challenge' | 'request' }
  | { kind: 'proposal'; proposalId: string; date: string };

type RespondItem =
  | { kind: 'gambit'; gambit: BaseGambitDto; respondBadge: 'challenge' | 'invitation' }
  | { kind: 'proposal'; proposal: SegmentProposal; date: string };

interface PendingRef {
  id: string;
  invitationType: 'challenge' | 'request';
}

interface PendingItem {
  gambit: BaseGambitDto;
  pendingBadge: 'challenge' | 'invitation';
}

const upcoming = useBatchLoader<{ id: string }, BaseGambitDto>({
  batchSize: GAMBIT_BATCH_SIZE,
  loadGeneration,
  fetchBatch: fetchGambitsById,
});

const respond = useBatchLoader<RespondRef, RespondItem>({
  batchSize: GAMBIT_BATCH_SIZE,
  loadGeneration,
  fetchBatch: async (refs) =>
    Promise.all(
      refs.map(async (r): Promise<RespondItem | null> => {
        if (r.kind === 'proposal') {
          const result = await gambits.sparkService.getProposal(r.proposalId);
          return result?.data ? { kind: 'proposal', proposal: result.data, date: r.date } : null;
        }
        const [gambit] = await fetchGambitsById([r]);
        return gambit
          ? {
              kind: 'gambit',
              gambit,
              respondBadge: r.invitationType === 'challenge' ? 'challenge' : 'invitation',
            }
          : null;
      }),
    ),
});

const pending = useBatchLoader<PendingRef, PendingItem>({
  batchSize: GAMBIT_BATCH_SIZE,
  loadGeneration,
  fetchBatch: async (refs) => {
    const gambits = await fetchGambitsById(refs);
    return refs.map((r, i) => {
      const gambit = gambits[i];
      if (!gambit) return null;
      return {
        gambit,
        pendingBadge: (r.invitationType === 'challenge' ? 'challenge' : 'invitation') as 'challenge' | 'invitation',
      };
    });
  },
});

// Top-level aliases for template ref binding
const gambitSentinel = upcoming.sentinel;
const respondSentinel = respond.sentinel;
const pendingSentinel = pending.sentinel;

const handshakeList = ref<InstanceType<typeof HandshakeList>>();

const isMyProfile = computed(() => {
  return user.value?.id === playerDetails.value?.id;
});

const profileCollection = computed(() =>
  collectPositions(playerDetails.value?.handshakes ?? [], playerDetails.value?.id ?? ''),
);

const isUniquePair = computed(() => {
  const viewerId = user.value?.id;
  const profileId = playerDetails.value?.id;
  if (!viewerId || !profileId || viewerId === profileId) return false;

  return profileCollection.value.uniquePartnerIds.includes(viewerId);
});

const isPaired = computed(() => {
  const viewerId = user.value?.id;
  const profileId = playerDetails.value?.id;
  if (!viewerId || !profileId || viewerId === profileId) return false;

  return profileCollection.value.playedPartnerIds.includes(viewerId);
});

const hasRespondSection = computed(
  () => isMyProfile.value && (respond.items.value.length > 0 || respond.isLoading.value),
);
const hasUpcomingSection = computed(() => upcoming.items.value.length > 0 || upcoming.isLoading.value);
const hasPendingSection = computed(
  () => isMyProfile.value && (pending.items.value.length > 0 || pending.isLoading.value),
);

const hasVisibleSections = computed(
  () => hasRespondSection.value || hasUpcomingSection.value || hasPendingSection.value,
);

/** How many card bands sit above the handshake list, so its first row continues the stripe. */
const profileBandCount = computed(
  () => Number(hasRespondSection.value) + Number(hasUpcomingSection.value) + Number(hasPendingSection.value),
);

const pendingHandshakes = computed(() => {
  return (
    (playerDetails.value as any as HandshakeFields)?.pendingHandshakes?.filter(
      (h) => h.responderId === user.value?.id || h.initiatorId === user.value?.id,
    ) || null
  );
});

const pendingHandshake = ref<HandshakeDto | null>(null);
const showTimestamp = ref<boolean>(true);

let hasBeenMountedOnce = false;
/** False while keep-alive holds this profile off screen; onActivated catches it up on return. */
let isActive = false;
const { absorb: absorbGhostClick } = useGhostClickGuard();

/** Resume events can arrive in bursts; one refresh per burst is enough. */
const RESUME_REFRESH_MIN_MS = 5_000;
let lastLoadStartedAt = 0;

function onPageVisible() {
  if (document.visibilityState !== 'visible' || !isActive) return;
  if (Date.now() - lastLoadStartedAt < RESUME_REFRESH_MIN_MS) return;
  refreshPlayerDetail();
}

onMounted(() => {
  absorbGhostClick();
  isActive = true;
  getPlayerDetail();
  hasBeenMountedOnce = true;
  document.addEventListener('click', onClickOutsideDetails);
  document.addEventListener('visibilitychange', onPageVisible);
});

watch(
  () => route.query.boards,
  (val) => {
    if (val === 'ongoing' || val === 'completed') {
      boardsTab.value = val;
      showBoardsOverlay.value = true;
    } else if (val === 'open') {
      boardsTab.value = 'ongoing';
      showBoardsOverlay.value = true;
      router.replace({ ...route, query: { ...route.query, boards: 'ongoing' } });
    } else {
      showBoardsOverlay.value = false;
    }
  },
  { immediate: true },
);

watch(
  () => route.query.players,
  (val) => {
    if (val === 'followers' || val === 'following') {
      showCollection.value = false;
      openPlayersModal(val);
    } else if (
      val === 'handshakes' ||
      val === 'positions' ||
      val === 'overview' ||
      val === 'milestones' ||
      val === 'people' ||
      val === 'boards'
    ) {
      showPlayersModal.value = false;
      collectionTab.value = val;
      showCollection.value = true;
    } else {
      showCollection.value = false;
    }
  },
  { immediate: true },
);

onActivated(() => {
  isActive = true;
  if (!hasBeenMountedOnce) return;
  refreshPlayerDetail();
});

onDeactivated(() => {
  isActive = false;
});

const isModalOpen = ref(false);

const setModalOpen = (open: boolean) => {
  if (isModalOpen.value === open) return;
  isModalOpen.value = open;
  if (open) markHandshakeModalOpen();
  else markHandshakeModalClosed();
};

onBeforeUnmount(() => setModalOpen(false));

const { open: openHandshakeModal, close } = useModal({
  component: HandshakeModal,
  attrs: {
    player: playerDetails as any as Ref<AttendeeDto>,
    handshake: pendingHandshake,
    showTimestamp: showTimestamp,
    onClose: async (options?: { refresh: boolean }) => {
      setModalOpen(false);
      if (pendingHandshake.value) {
        markHandshakeShown(pendingHandshake.value.id);
      }
      pendingHandshake.value = null;
      close();
      if (options?.refresh) {
        await getPlayerDetail();
      }
    },
  },
});

watch(
  () => playerDetails.value?.id,
  (id, oldId) => {
    if (id && id !== oldId) {
      startUserDocListener(id);
    }
  },
);

watch(
  pendingHandshakes,
  (handshakes) => {
    // The offer on screen can go out from under us: when the two of them
    // offered at the same moment and the other side confirmed theirs, this row
    // is folded away rather than made. Anything still on record, offered or
    // shaken, leaves the modal where it is.
    if (isModalOpen.value && pendingHandshake.value && !pendingHandshake.value.isConfirmed) {
      const onRecord = [...(playerDetails.value?.handshakes || []), ...(handshakes || [])];

      if (!onRecord.some((h) => h.id === pendingHandshake.value!.id)) {
        setModalOpen(false);
        pendingHandshake.value = null;
        close();
        void getPlayerDetail();
        return;
      }
    }

    if (isHandshakeModalOpen()) return;

    // Code-based pending handshakes (no gambit/spot) complete when both
    // players type codes — don't open the offer/accept modal for those.
    const handshake = handshakes?.find(
      (h) => h.responderId === user.value?.id && !wasHandshakeShown(h.id) && !!(h.gambitId || h.locationId),
    );
    if (!handshake) return;

    handshakeQrModalRef.value?.close();

    markHandshakeShown(handshake.id);
    pendingHandshake.value = handshake;
    setModalOpen(true);
    openHandshakeModal();
  },
  { immediate: true },
);

let unsubscribeUserDoc: (() => void) | null = null;
let lastUserDocUpdatedAt: number | null = null;

function startUserDocListener(userId: string) {
  stopUserDocListener();
  lastUserDocUpdatedAt = null;

  const docRef = doc(firestore, 'users', userId);
  unsubscribeUserDoc = onSnapshot(docRef, (docSnap) => {
    if (!docSnap.exists()) return;
    const data = docSnap.data();
    const updatedAt = data.updatedAt?.toMillis?.() ?? data.updatedAt?.getTime?.() ?? 0;

    if (isActive && lastUserDocUpdatedAt !== null && updatedAt > lastUserDocUpdatedAt) {
      refreshPlayerDetail();
    }
    lastUserDocUpdatedAt = updatedAt;
  });
}

function stopUserDocListener() {
  if (unsubscribeUserDoc) {
    unsubscribeUserDoc();
    unsubscribeUserDoc = null;
  }
}

const HANDSHAKE_POLL_MS = 2_000;
let handshakePollTimer: ReturnType<typeof setInterval> | null = null;

function startHandshakePolling() {
  stopHandshakePolling();
  handshakePollTimer = setInterval(() => {
    refreshProfileOnly();
  }, HANDSHAKE_POLL_MS);
}

function stopHandshakePolling() {
  if (handshakePollTimer) {
    clearInterval(handshakePollTimer);
    handshakePollTimer = null;
  }
}

function onQrVisibleChange(visible: boolean) {
  if (visible) {
    startHandshakePolling();
    return;
  }

  stopHandshakePolling();
  refreshPlayerDetail();
}

function onHandshakeCreated(handshake: HandshakeDto, existing?: boolean) {
  if (!handshake.isConfirmed) {
    refreshPlayerDetail();
    return;
  }
  rememberHandshake(handshake.id);
  closePlayersModal();
  refreshPlayerDetail();
  if (existing) void openLiveHandshakeClock(handshake.id, localize('handshake.player.passerby'));
}

/** The same counts as the collection overview, so the badge and the overlay never disagree. */
const collectionCounts = computed(() => {
  const handshakes = playerDetails.value?.handshakes ?? [];
  const collection = profileCollection.value;
  const uniquePeople = collection.people.unique + collection.people.reported;

  return {
    pairs: collection.pairCount,
    unpaired: Math.max(0, 960 - collection.pairCount),
    positions: new Set(handshakes.flatMap((handshake) => (handshake.isConfirmed ? (handshake.positions ?? []) : [])))
      .size,
    people: collection.peopleTotal,
    isMaster: collection.uniqueCount >= 960 && uniquePeople >= 960,
  };
});

/** "Unpaired 954": positions still missing from the 960 unique pairs. */
const unpairedLabel = computed(() => `${localize('attendee.badge.unpaired')} ${collectionCounts.value.unpaired}`);

/** Spoken name of the challenge button, since the top line is only the counts. */
const handshakeCountsLabel = computed(
  () =>
    `${localize('handshake.collection.title')}, ${collectionCounts.value.pairs} ${localize('handshake.collection.tabs.pairs')}, ${collectionCounts.value.positions} ${localize('player.details.media.tabs.positions')}, ${collectionCounts.value.people} ${localize('handshake.collection.tabs.people')}`,
);

watch(
  () => (playerDetails.value as any)?.handshakes as HandshakeDto[] | undefined,
  (next, previous) => {
    // Off this profile a photo can reload the list. That is not a new handshake,
    // and it must not be marked seen before this player has celebrated it.
    if (!previous) {
      celebrateNewlyConfirmedHandshakes(previous, next, user.value?.id, fireConfetti);
      return;
    }
    if (!isActive) return;

    if (celebrateNewlyConfirmedHandshakes(previous, next, user.value?.id, fireConfetti)) {
      handshakeQrModalRef.value?.close();
      closePlayersModal();
    }
  },
);

watch(
  () => ({
    isListLoading: isListLoading.value,
    isDetailLoading: isDetailLoading.value,
    username: String(route.params.username),
    players: itemList.value,
    playerDetails: playerDetails.value,
  }),
  ({ isListLoading, isDetailLoading, username, players, playerDetails }) => {
    if (isListLoading || isDetailLoading || !playerDetails || !username) return;

    const existingPlayer = players?.find((p) => p.username === playerDetails.username);
    if (!existingPlayer && username && !tableParams.value.search) {
      nextTick(() => {
        tableStore.insertItem(playerDetails as BaseUserDto);
      });
    }
  },
);

const loadPlayerDetail = async (silent: boolean) => {
  if (!props.username) return;

  loadGeneration.value++;
  const gen = loadGeneration.value;
  lastLoadStartedAt = Date.now();

  if (!silent) {
    upcoming.reset();
    respond.reset();
    pending.reset();
    isDetailLoading.value = true;
    isNotFound.value = false;
    if (playerDetails.value?.username !== props.username) {
      playerDetails.value = playerStore.getCachedProfile(props.username) ?? null;
    }
  }

  const result = await gambits.userService.getUserByName(props.username);
  if (gen !== loadGeneration.value) return;

  if (!silent) {
    isDetailLoading.value = false;
    if (result.error?.code === 404 || result.error?.code === 500) {
      isNotFound.value = true;
      return;
    }
  }
  if (!result.data) return;

  playerDetails.value = result.data;
  playerStore.setCachedProfile(props.username, result.data);

  if (isMyProfile.value) {
    const [respondResult, sparkRespondResult] = await Promise.all([
      gambits.gambitService.getRespondInvitations(),
      gambits.sparkService.getRespondSparkInvitations(),
    ]);
    if (gen !== loadGeneration.value) return;
    if (!silent) {
      if (respondResult.error) logger.warn('Failed to load respond invitations: %o', respondResult.error);
      if (sparkRespondResult.error)
        logger.warn('Failed to load spark respond invitations: %o', sparkRespondResult.error);
    }

    const gambitRespondRefs: RespondRef[] = (respondResult.data || []).map((r) => ({
      kind: 'gambit' as const,
      id: r.id,
      invitationType: r.invitationType,
    }));
    const sparkRespondRefs: RespondRef[] = (sparkRespondResult.data || []).map((r) => ({
      kind: 'proposal' as const,
      proposalId: r.proposalId,
      date: r.date,
    }));
    const merged = [...gambitRespondRefs, ...sparkRespondRefs];

    if (silent) {
      await respond.refresh(merged);
    } else if (merged.length) {
      respond.allRefs.value = merged;
      respond.isLoading.value = true;
      await respond.loadNext();
      if (gen !== loadGeneration.value) return;
      respond.isLoading.value = false;
    }
    if (gen !== loadGeneration.value) return;
  }

  if (result.data.upcomingGambits?.length) {
    if (silent) {
      await upcoming.refresh(result.data.upcomingGambits);
    } else {
      upcoming.allRefs.value = result.data.upcomingGambits;
      upcoming.isLoading.value = true;
      await upcoming.loadNext();
      upcoming.isLoading.value = false;
    }
    if (gen !== loadGeneration.value) return;
  } else if (silent) {
    upcoming.reset();
  }

  if (isMyProfile.value) {
    const pendingInvitationsResult = await gambits.gambitService.getOutgoingInvitations(playerDetails.value!.id);
    if (gen !== loadGeneration.value) return;
    if (!silent && pendingInvitationsResult.error) {
      logger.warn('Failed to load pending invitations: %o', pendingInvitationsResult.error);
    }

    const refs: PendingRef[] = (pendingInvitationsResult.data || []).map((g) => ({
      id: g.id,
      invitationType: g.invitationType,
    }));

    if (silent) {
      await pending.refresh(refs);
    } else if (refs.length) {
      pending.allRefs.value = refs;
      pending.isLoading.value = true;
      await pending.loadNext();
      if (gen !== loadGeneration.value) return;
      pending.isLoading.value = false;
    }
    if (gen !== loadGeneration.value) return;
  }
};

const getPlayerDetail = () => loadPlayerDetail(false);
const refreshPlayerDetail = () => loadPlayerDetail(true);

/**
 * Handshakes live on the profile itself, so the QR poll only needs the profile
 * and can leave the card sections (a dozen-plus requests) alone.
 */
const refreshProfileOnly = async () => {
  const username = props.username;
  if (!username) return;

  const gen = loadGeneration.value;
  const result = await gambits.userService.getUserByName(username);
  if (gen !== loadGeneration.value || username !== props.username || !result.data) return;

  playerDetails.value = result.data;
  playerStore.setCachedProfile(username, result.data);
};

onBeforeUnmount(() => {
  upcoming.stop();
  respond.stop();
  pending.stop();
  stopUserDocListener();
  stopHandshakePolling();
  document.removeEventListener('click', onClickOutsideDetails);
  document.removeEventListener('visibilitychange', onPageVisible);
});

const navigateToSparks = () => {
  router.push({
    name: RouterNameType.SPARKS,
  });
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.header-container {
  position: relative;
  z-index: 501;
  background: transparent;
  border-radius: 0;
}

.detail-container:not(.is-mobile) .header-container {
  border-radius: 0;
}

.header-container :deep(.player-detail-header) {
  background: transparent;
  border-radius: 0;
}

.profile-corner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.profile-unpaired {
  display: flex;
  align-items: center;
  height: 34px;
  color: var(--color-white);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;

  .un {
    color: var(--color-pink);
  }

  .count {
    margin-left: 0.4em;
    font-variant-numeric: tabular-nums;
  }
}

/* A small chip under the count. Gray until you have played, then white, or pink for a unique pair. */
.profile-bond {
  padding: 4px 8px;
  border: 0;
  border-radius: var(--radius-normal);
  background: var(--color-white-8);
  color: var(--color-white-40);
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
  line-height: 16px;
  cursor: pointer;

  &.is-repeat {
    color: var(--color-white);
  }

  &.is-unique {
    background: var(--color-linear-gradient-10);
    color: var(--color-pink);

    @include hover {
      background: var(--color-linear-gradient-30);
    }
  }

  @include hover {
    background: var(--color-white-10);
  }
}

.header-container :deep(.action-bar) {
  background: transparent;
  border-radius: 0;
  padding-top: 0;
}

.age-and-follows-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 10px var(--spacing-h);
  background: transparent;
}

.age-and-follows-section .panel-username {
  max-width: 100%;
  color: var(--color-white);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.age-and-follows-section .panel-username + .name-and-age {
  margin-top: 4px;
}

.age-and-follows-section .name-and-age {
  display: flex;
  align-items: center;
  gap: 0.2em;
  font-size: 11px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.age-and-follows-section .player-name {
  color: var(--color-white-40);
  text-transform: capitalize;
}

.age-and-follows-section .age-comma {
  color: var(--color-white-40);
}

.age-and-follows-section .age {
  color: var(--color-white-40);
}

#news-button {
  position: absolute;

  // right: var(--spacing-xxl);
  // bottom: var(--spacing-l);

  // top: 24px;
  right: 24px;
  bottom: 24px;

  z-index: 4;

  :deep(.gam-button-container) {
    padding-left: 40px;
    padding-right: 23px;
  }

  :deep(.label) {
    display: flex;
    gap: var(--spacing-md);
  }
}

.metric-group {
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  gap: var(--spacing-xs);
  width: 100%;
}

.right-side-panel,
.gam-form-content {
  width: 100%;
  height: calc(100% - 5rem);
}

.profile-handshake-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-white);
  background: rgba(255, 255, 255, 0.06);
  transition: all 150ms linear;

  @include hover {
    background: var(--color-white-20);
  }
}

/* The 960 disc, larger so it leads the stack and breaks the capsule. */
.clock-disc {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  color: var(--color-white-70);
  @include raised-action(gray);

  @include hover {
    color: var(--color-white);
  }

  @include press;
}

.pendant-play {
  display: flex;
  align-items: center;
  justify-content: center;

  :deep(.circle-cta) {
    border-radius: 50% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;

    .icon {
      position: static !important;
      transform: none !important;
    }

    .svg-container {
      font-size: 24px !important;
      width: 24px !important;
      height: 24px !important;
    }
  }
}

.image-cta-boards,
.image-cta-walks,
.image-cta-handshake {
  position: absolute;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-dark-5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--color-white-10);
  cursor: pointer;
  z-index: 2;

  @include press;
}

.image-cta-boards {
  left: -12px;
  bottom: 15%;
}

.image-cta-walks {
  bottom: 8%;
  right: -8px;
}

.image-cta-handshake {
  right: -4px;
  top: -4px;
}

.gambit-sentinel {
  flex-shrink: 0;
}

.base-info {
  position: relative;
  z-index: 501;
  display: flex;
  flex-direction: column;
  // gap: var(--spacing-xs);
}

.detail-container {
  position: relative;
  padding: 0 0 calc(2 * var(--spacing-xxl));
  margin: 0 auto;
  /* Photo is 128px at spacing-l. The 44px glass hangs 10px under it. */
  --pendant-center: calc(var(--spacing-l) + 128px + 10px + 22px);
}

/* Pinned to the sheet, not the header, so scrolling the profile leaves it put. */
.profile-nav-rail {
  position: absolute;
  z-index: 502;
  top: 0;
  right: var(--spacing-l);
  bottom: 0;
  width: var(--nav-cell-size, 3rem);
  padding-top: var(--pendant-center);
  pointer-events: none;

  :deep(.profile-nav) {
    position: sticky;
    top: var(--pendant-center);
    transform: translateY(-50%);
    pointer-events: auto;
    overflow: visible;
  }

  :deep(.clock-lead) {
    z-index: 2;
  }
}

.detail-container:not(.is-mobile) {
  max-width: 42rem;
}

.detail-info {
  position: relative;
  z-index: 500;
  width: 100%;
  padding: 0 var(--spacing-l);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.about-me {
  width: 100%;
  margin-top: 8px;
  color: var(--color-white-50);
  font-style: italic;
  font-weight: 200;
  font-size: 13px;
  letter-spacing: 0.03em;
  line-height: 1.5;
  text-align: left;
  white-space: pre-line;
}

.upcoming-gambit {
  box-shadow: 0 8px 6px color-mix(in srgb, var(--color-dark-1000) 33%, transparent);
}

.profile-gambits {
  display: flex;
  flex-direction: column;

  :deep(.gam-gambit-card.needs-respond) {
    border: 2px solid var(--color-pink);
  }
}

.latest-handshakes {
  display: flex;
  flex-direction: column;

  &:not(.has-gambits) > .open-handshakes {
    margin-bottom: var(--spacing-xl);
  }

  /* No card band above, so the heading takes the place of that band's top padding. */
  &:not(.has-gambits) :deep(.section-title) {
    padding-top: 0;
  }
}

.board-squares-icon {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  overflow: hidden;
  background-repeat: no-repeat;
  background-size: 50% 50%;
  background-position:
    0 0,
    100% 0,
    0 100%,
    100% 100%;
  background-image: linear-gradient(#f0d9b5, #f0d9b5), linear-gradient(#b58863, #b58863),
    linear-gradient(#b58863, #b58863), linear-gradient(#f0d9b5, #f0d9b5);
}

.puzzle-squares-icon {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  overflow: hidden;

  .psi-cell {
    width: 100%;
    height: 100%;
  }

  .psi-gray {
    background: rgba(255, 255, 255, 0.1);
  }

  .psi-green {
    background: #2ecc71;
  }

  .psi-red {
    background: #e74c3c;
  }
}

.trace-squares-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  width: 20px;
  height: 20px;
  border-radius: 3px;
  overflow: hidden;

  .trace-square {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .trace-square-other {
    background-color: #f5c542;
  }

  .trace-square-other.trace-square-solved {
    background-color: #f5c542;
    opacity: 0.4;
  }

  .trace-square-mine {
    background-color: oklch(0.7818 0.2418 355);
  }

  .trace-square-mine-solved {
    background-color: oklch(0.7818 0.2418 355);
    opacity: 0.4;
  }
}
</style>
